/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState } from 'react';
import { Loader2, UserPlus, Trash2, ChevronDown, Building2, LayoutGrid } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  engineer: 'Engineer',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  engineer: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  viewer: 'bg-muted text-muted-foreground border-border',
};

const ERROR_MESSAGES: Record<string, string> = {
  ORG_ROLE_MISSING: "You don't have permission to remove members",
  PROJECT_ACCESS_DENIED: "You don't have access to this project",
};

interface OrgMember {
  id: string;
  userId: string;
  email?: string | undefined;
  name?: string | undefined;
  role: string;
  joinedAt: string;
  scope: 'org';
}

interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  email?: string | undefined;
  name?: string | undefined;
  role: string;
  addedAt: string;
}

interface OrgProject {
  id: string;
  name: string;
}

interface TeamManagerProps {
  orgId: string;
  orgName: string;
  currentUserId: string;
  myRole: string;
  initialMembers: OrgMember[];
  orgProjects: OrgProject[];
  initialProjectMembers: ProjectMember[];
}

export function TeamManager({
  orgId,
  orgName,
  currentUserId,
  myRole,
  initialMembers,
  orgProjects,
  initialProjectMembers,
}: TeamManagerProps): import('react/jsx-runtime').JSX.Element {
  const [members, setMembers] = useState<OrgMember[]>(initialMembers);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>(initialProjectMembers);

  // Invite form state
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [scope, setScope] = useState<'org' | 'apps'>('org');
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [removingOrgId, setRemovingOrgId] = useState<string | null>(null);
  const [removingProjId, setRemovingProjId] = useState<string | null>(null);
  const [removeProjectError, setRemoveProjectError] = useState<string | null>(null);

  const canManage = myRole === 'owner' || myRole === 'engineer';

  function toggleApp(appId: string): void {
    setSelectedApps((prev): Set<string> => {
      const next = new Set(prev);
      if (next.has(appId)) {
        next.delete(appId);
      } else {
        next.add(appId);
      }
      return next;
    });
  }

  async function invite(): Promise<void> {
    if (!email.trim()) return;
    if (scope === 'apps' && selectedApps.size === 0) {
      setInviteError('Select at least one app.');
      return;
    }
    setInviting(true);
    setInviteError(null);

    try {
      if (scope === 'org') {
        const res = await fetch(`/api/orgs/${orgId}/invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), role: inviteRole }),
        });
        const data = await res.json();
        if (!res.ok) {
          setInviteError(data.error ?? 'Failed to invite');
          return;
        }
        setMembers((prev) => {
          const existing = prev.findIndex((m): boolean => m.userId === data.user_id);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = { ...updated[existing]!, role: data.role } as OrgMember;
            return updated;
          }
          return [
            ...prev,
            {
              id: data.id,
              userId: data.user_id,
              role: data.role,
              joinedAt: data.joined_at,
              scope: 'org',
            },
          ];
        });
      } else {
        const results = await Promise.all(
          Array.from(selectedApps).map((projectId) =>
            fetch(`/api/projects/${projectId}/members`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email.trim(), role: inviteRole }),
            }).then((r) => r.json().then((d) => ({ ok: r.ok, data: d, projectId }))),
          ),
        );
        const failed = results.filter((r): boolean => !r.ok);
        if (failed.length > 0) {
          setInviteError(failed[0]!.data?.error ?? 'Failed to invite to some apps');
          return;
        }
        setProjectMembers((prev): ProjectMember[] => {
          let next = [...prev];
          for (const { data, projectId } of results) {
            const existing = next.findIndex(
              (m): boolean => m.userId === data.user_id && m.projectId === projectId,
            );
            if (existing >= 0) {
              next[existing] = { ...next[existing]!, role: data.role } as ProjectMember;
            } else {
              next = [
                ...next,
                {
                  id: data.id,
                  projectId,
                  userId: data.user_id,
                  role: data.role,
                  addedAt: data.added_at,
                },
              ];
            }
          }
          return next;
        });
      }
      setEmail('');
      setSelectedApps(new Set());
    } finally {
      setInviting(false);
    }
  }

  async function removeOrgMember(memberId: string): Promise<void> {
    if (!confirm('Remove this member from the organization?')) return;
    setRemovingOrgId(memberId);
    try {
      const member = members.find((m): boolean => m.id === memberId);
      if (!member) return;
      await fetch(`/api/orgs/${orgId}/members/${member.userId}`, { method: 'DELETE' });
      setMembers((prev): OrgMember[] => prev.filter((m): boolean => m.id !== memberId));
    } finally {
      setRemovingOrgId(null);
    }
  }

  async function removeProjectMember(pm: ProjectMember): Promise<void> {
    if (!confirm('Remove access to this app?')) return;
    setRemovingProjId(pm.id);
    setRemoveProjectError(null);
    try {
      const res = await fetch(`/api/projects/${pm.projectId}/members?user_id=${pm.userId}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as {
          code?: string;
          message?: string;
        } | null;
        if (data?.code != null) {
          const friendly = ERROR_MESSAGES[data.code] ?? 'Unable to remove member';
          setRemoveProjectError(`${friendly} (${data.code}).`);
        } else {
          setRemoveProjectError(data?.message ?? 'Unable to remove member.');
        }
        return;
      }

      setProjectMembers((prev): ProjectMember[] => prev.filter((m): boolean => m.id !== pm.id));
    } finally {
      setRemovingProjId(null);
    }
  }

  async function changeOrgRole(memberId: string, newRole: string): Promise<void> {
    const member = members.find((m): boolean => m.id === memberId);
    if (!member) return;
    const res = await fetch(`/api/orgs/${orgId}/members/${member.userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: newRole }),
    });
    if (res.ok) {
      setMembers((prev): OrgMember[] =>
        prev.map((m): OrgMember => (m.id === memberId ? { ...m, role: newRole } : m)),
      );
    }
  }

  // Group project members by userId for display
  const projectMembersByUser = new Map<string, ProjectMember[]>();
  for (const pm of projectMembers) {
    const list = projectMembersByUser.get(pm.userId) ?? [];
    list.push(pm);
    projectMembersByUser.set(pm.userId, list);
  }

  // Users with only project-level access (not org members)
  const orgMemberUserIds = new Set(members.map((m): string => m.userId));
  const projectOnlyUserIds = Array.from(projectMembersByUser.keys()).filter(
    (uid): boolean => !orgMemberUserIds.has(uid),
  );

  const projectById = new Map(orgProjects.map((p): [string, OrgProject] => [p.id, p]));
  const totalCount = members.length + projectOnlyUserIds.length;

  return (
    <div className="space-y-4">
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Invite member</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Scope toggle */}
            <div className="flex gap-2">
              <button
                onClick={(): void => setScope('org')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                  scope === 'org'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                <Building2 className="h-3.5 w-3.5" />
                All apps in {orgName}
              </button>
              <button
                onClick={(): void => setScope('apps')}
                disabled={orgProjects.length === 0}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors disabled:opacity-40 ${
                  scope === 'apps'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Specific apps
              </button>
            </div>

            {/* App selector */}
            {scope === 'apps' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Select apps to grant access to
                </Label>
                <div className="flex flex-wrap gap-2">
                  {orgProjects.map((app): import('react/jsx-runtime').JSX.Element => (
                    <button
                      key={app.id}
                      onClick={(): void => toggleApp(app.id)}
                      className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                        selectedApps.has(app.id)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {app.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Email + role + send */}
            <div className="flex gap-2">
              <Input
                placeholder="colleague@example.com"
                value={email}
                onChange={(e): void => setEmail(e.target.value)}
                onKeyDown={(e): false | Promise<void> => e.key === 'Enter' && invite()}
                className="flex-1"
              />
              <select
                value={inviteRole}
                onChange={(e): void => setInviteRole(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {scope === 'org' && myRole === 'owner' && <option value="owner">Owner</option>}
                <option value="engineer">Engineer</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button
                onClick={invite}
                disabled={
                  inviting || !email.trim() || (scope === 'apps' && selectedApps.size === 0)
                }
                size="sm"
              >
                {inviting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                <span className="ml-1.5">Invite</span>
              </Button>
            </div>

            {inviteError != null && <p className="text-xs text-destructive">{inviteError}</p>}

            <p className="text-xs text-muted-foreground">
              {scope === 'org' ? (
                <>
                  <strong>Owner</strong> — full control. &nbsp;
                  <strong>Engineer</strong> — create/update apps, invite members. &nbsp;
                  <strong>Viewer</strong> — read-only access.
                </>
              ) : (
                <>
                  <strong>Engineer</strong> — update app settings, rotate secrets. &nbsp;
                  <strong>Viewer</strong> — read-only access to selected apps.
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Members list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center justify-between">
            Members
            <span className="text-xs font-normal text-muted-foreground">{totalCount}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {removeProjectError != null && (
            <p
              className="px-4 py-2 text-xs text-destructive"
              data-testid="project-member-remove-error"
            >
              {removeProjectError}
            </p>
          )}
          <ul className="divide-y divide-border/40">
            {members.map((member): import('react/jsx-runtime').JSX.Element => {
              const isMe = member.userId === currentUserId;
              const isOwner = member.role === 'owner';
              const canRemove = canManage && !isMe && !(isOwner && myRole !== 'owner');
              const canChangeRole = myRole === 'owner' && !isMe;

              return (
                <li key={member.id} className="flex items-center justify-between px-4 py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold uppercase shrink-0">
                      {(member.name ?? member.email ?? member.userId).slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm truncate">
                          {member.name != null
                            ? `${member.name} (${member.email})`
                            : (member.email ?? member.userId)}
                        </p>
                        <span className="text-[9px] rounded-full border px-1.5 py-0 text-muted-foreground">
                          Org-wide
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Joined {new Date(member.joinedAt).toLocaleDateString()}
                        {isMe && ' (you)'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {canChangeRole ? (
                      <div className="relative">
                        <select
                          value={member.role}
                          onChange={(e): Promise<void> => changeOrgRole(member.id, e.target.value)}
                          className="appearance-none rounded-full border px-2.5 py-0.5 text-[11px] font-medium pr-5 bg-background cursor-pointer"
                        >
                          <option value="owner">Owner</option>
                          <option value="engineer">Engineer</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 pointer-events-none text-muted-foreground" />
                      </div>
                    ) : (
                      <Badge
                        className={`text-[10px] rounded-full ${ROLE_COLORS[member.role] ?? ROLE_COLORS['viewer']}`}
                      >
                        {ROLE_LABELS[member.role] ?? member.role}
                      </Badge>
                    )}
                    {canRemove && (
                      <button
                        onClick={(): Promise<void> => removeOrgMember(member.id)}
                        disabled={removingOrgId === member.id}
                        className="p-1 rounded text-muted-foreground/50 hover:text-destructive transition-colors disabled:opacity-50"
                        title="Remove from org"
                      >
                        {removingOrgId === member.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}

            {/* Project-scoped-only members */}
            {projectOnlyUserIds.map((userId): import('react/jsx-runtime').JSX.Element => {
              const userPMs = projectMembersByUser.get(userId) ?? [];
              const isMe = userId === currentUserId;

              return (
                <li key={userId} className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold uppercase shrink-0">
                      {userId.slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-mono truncate">
                        {userId.slice(0, 20)}…{isMe ? ' (you)' : ''}
                      </p>
                      <p className="text-[10px] text-muted-foreground">App-level access only</p>
                    </div>
                  </div>
                  <div className="mt-2 ml-10 flex flex-wrap gap-1.5">
                    {userPMs.map((pm): import('react/jsx-runtime').JSX.Element => {
                      const app = projectById.get(pm.projectId);
                      return (
                        <span
                          key={pm.id}
                          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground"
                        >
                          {app?.name ?? pm.projectId.slice(0, 8)}
                          <span
                            className={`font-medium ${pm.role === 'engineer' ? 'text-blue-400' : ''}`}
                          >
                            · {pm.role}
                          </span>
                          {canManage && (
                            <button
                              onClick={(): Promise<void> => removeProjectMember(pm)}
                              disabled={removingProjId === pm.id}
                              className="ml-0.5 text-muted-foreground/50 hover:text-destructive transition-colors"
                              title={`Remove from ${app?.name}`}
                            >
                              {removingProjId === pm.id ? (
                                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                              ) : (
                                '×'
                              )}
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </li>
              );
            })}

            {totalCount === 0 && (
              <li className="px-4 py-6 text-center text-xs text-muted-foreground">
                No members yet.
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
